// apps/web/src/widgets/option-set/renderer.tsx
//
// Phase 6 — WIDG-04 OptionSetRenderer (D-01 typed config). Multi-select Pill
// grid driven by `config.cardinality`. Per UI-SPEC §"option-set renderer":
//   - empty options[] → "No options to pick from." fallback
//   - no selection   → "(none selected)" hint
//   - at-max         → unselected pills disabled + "{N} selected — limit reached"
import { Pill } from "@/components/ui/pill";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { toggle as toggleSelection } from "./toggle.js";

export function OptionSetRenderer({
  widget,
  config,
  state,
  onStateChange,
}: WidgetRendererProps<"option-set">): React.ReactElement {
  const selected: ReadonlyArray<string> = state?.selected ?? [];
  const max = config.cardinality.max;
  const min = config.cardinality.min;
  const atMax = max !== undefined && selected.length >= max;

  // Thin wrapper over the shared pure toggle helper (option-set/toggle.ts).
  // The helper owns the cardinality guards (single-select swap + at-min/at-max);
  // the renderer keeps owning the onStateChange side effect. A no-op guard
  // (same length + same membership) suppresses redundant state churn so a
  // blocked click stays a true no-op — preserving the existing browser-test
  // behavior (06-REVIEW WR-02).
  const toggle = (id: string): void => {
    const next = toggleSelection(selected, id, { min, max });
    const unchanged =
      next.length === selected.length && next.every((x, i) => x === selected[i]);
    if (unchanged) {
      return;
    }
    onStateChange({ selected: next });
  };

  const labelId = `${widget.instanceId}-label`;

  // <section role="group"> is the UI-SPEC §"option-set renderer" contract. A
  // native <fieldset> would inject legend styling and constrain inner layout;
  // the option-set is a recipe widget grouping, not a form field group.
  return (
    // biome-ignore lint/a11y/useSemanticElements: see comment above — Phase 6 design system choice
    <section role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
      <h3 id={labelId} className="text-base font-semibold text-[var(--recipe-ink)]">
        {widget.name}
      </h3>
      {config.options.length === 0 ? (
        <p className="text-sm text-[var(--recipe-muted)]">No options to pick from.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {config.options.map((opt) => {
              const isSelected = selected.includes(opt.id);
              // Single-select swap pattern (min === max === 1): unselected pills
              // stay clickable so the user can replace the current selection.
              // (06-REVIEW WR-02)
              const isSingleSelectSwap = min === 1 && max === 1;
              const isDisabled = !isSelected && atMax && !isSingleSelectSwap;
              return (
                <Pill
                  key={opt.id}
                  selected={isSelected}
                  disabled={isDisabled}
                  onClick={() => toggle(opt.id)}
                >
                  {opt.label}
                </Pill>
              );
            })}
          </div>
          {selected.length === 0 ? (
            <p className="text-xs italic text-[var(--recipe-muted)]">(none selected)</p>
          ) : null}
          {atMax && max !== undefined && !(min === 1 && max === 1) ? (
            <p className="text-xs text-[var(--recipe-muted)]">{max} selected — limit reached</p>
          ) : null}
        </>
      )}
    </section>
  );
}
